import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import Searchbar from '../Searchbar'
import { faSliders } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSearchParams } from 'react-router-dom'
import useRetailCollections from '@/Hooks/useRetailCollections'
import CatalogFilter from './CatalogFilter'
import useRetailFilters from '@/Hooks/useRetailFilters'
import usePrefStore from '@/stores/prefs'
import useSales from '@/Hooks/useSales'
import useRetailRoleForCustomer from '@/Hooks/useRetailRoleForCustomer'
import useRetailCatalogScope from '@/Hooks/useRetailCatalogScope'
import { RetailCatalogFilterContext } from '@/Contexts/RetailCatalogFilterContext';

import styles_Searchbar from '../Searchbar.module.scss';
import { GlobalContext } from '@/Contexts/GlobalContext';

function RetailCatalogSearchBar(props) {
   // Prefs store
   const { curCustomer } = usePrefStore()
   const { getCustomerById } = useSales()
   const { isRetailModelVisibleForCustomer } = useRetailCollections()

   // Scope catalogue Retail : cf. Hooks/useRetailCatalogScope.
   const { customerType: effectiveCustomerType, customerForGrid } = useRetailCatalogScope()
   const roleForCurCustomer = useRetailRoleForCustomer(curCustomer)

   const hasExcluBoutique = useMemo(() => {
      const customer = curCustomer ? getCustomerById(curCustomer) : null
      return customer?.exclu_boutique != null
   }, [curCustomer, getCustomerById])

   // Le filtre exclu boutique est visible si admin/N1/N2 du client courant, ou si le client a exclu_boutique
   const showExcluBoutiqueFilter = useMemo(() => {
      if (roleForCurCustomer.isAdmin || roleForCurCustomer.isN1 || roleForCurCustomer.isN2) return true
      return hasExcluBoutique
   }, [roleForCurCustomer, hasExcluBoutique])

   const {
      searchText,                // Texte de recherche
      setSearchText,
      paramCollectionId,
      setParamCollectionId,
      setParamGammeId,
      setParamFamilleId,
      gammes,                    // Toutes les gammes + état coché ou pas
      setGammes,
      collections,               // Toutes les collections + état coché ou pas
      setCollections,
      familles,                  // Toutes les familles + état coché ou pas
      setFamilles,
      typologies,                // Toutes les typologies + état coché ou pas
      setTypologies,
      cycles,                    // Tous les cycles + état coché ou pas
      setCycles,
      excluBoutique,             // Exclusivité boutique (null, 'yes', 'no', 'all')
      setExcluBoutique,
      setDoFilter,
      allMinMax,
      minPrice,
      setMinPrice,
      maxPrice,
      setMaxPrice,
      minManualPrice,
      setMinManualPrice,
      maxManualPrice,
      setMaxManualPrice,
      getSelectedGammes, getSelectedGammesIds, getSelectedCollections, getSelectedCollectionsIds, getSelectedFamilles, getSelectedFamillesIds, getSelectedTypologiesIds, getSelectedCyclesIds, clearGammes, clearCollections, clearFamilles, clearTypologies, clearCycles
   } = { ...RetailCatalogFilterContext() } as any

   // States
   const [isSearchFilterVisible, setIsSearchFilterVisible] = useState(false)
   const [filterPopup, setFilterPopup] = useState(null)
   const [hasCriteriaEnabled, setHasCriteriaEnabled] = useState(false)
   //
   const { getRetailCollectionsInGammes, getRetailGammes } = useRetailCollections()
   const [searchParams] = useSearchParams()
   const { filterOnCriteria } = useRetailFilters()
   const { isDarkLayerVisible, setIsDarkLayerVisible, setKeepSearchBarAccessible } = { ...GlobalContext() } as any

   /*******
    ******* CRÉATION DES LISTES (GAMMES, COLLECTIONS, FAMILLES)
    *******/

   // On prépare la liste de toutes les valeurs possibles pour les listes
   // On commence par les gammes, le reste va cascader
   useEffect(() => {
      const id = searchParams.get('collec')
      const id_gamme = searchParams.get('gamme')
      const id_famille = searchParams.get('fam')

      setParamCollectionId(id)
      setParamGammeId(id_gamme)
      setParamFamilleId(id_famille)
   }, [searchParams, setParamCollectionId, setParamGammeId, setParamFamilleId])

   useEffect(() => {
      if (paramCollectionId) {   // On est entré par une collection
         setHasCriteriaEnabled(getSelectedFamilles()?.length || getSelectedTypologiesIds()?.length || getSelectedCyclesIds()?.length || excluBoutique === 'yes' || excluBoutique === 'no' || (minManualPrice != null) || (maxManualPrice != null))
      } else {
         setHasCriteriaEnabled(getSelectedGammes()?.length || getSelectedCollections()?.length || getSelectedFamilles()?.length || getSelectedTypologiesIds()?.length || getSelectedCyclesIds()?.length || excluBoutique === 'yes' || excluBoutique === 'no' || (minManualPrice != null) || (maxManualPrice != null))
      }
   }, [getSelectedCollections, getSelectedCyclesIds, getSelectedTypologiesIds, getSelectedFamilles, getSelectedGammes, excluBoutique, maxManualPrice, minManualPrice, paramCollectionId])

   /*******
    ******* AFFICHAGE/MASQUAGE DES ITEMS DES LISTES (GAMMES, COLLECTIONS, FAMILLES)
    *******
    ******* Rien à faire pour les Gammes, c'est le point de départ du reste
    ******* Pour Collections, on fait un traitement sans appeler le filtrage
    ******* Pour Familles, on utilise l'aide du filtrage
    *******/

   // Affichage/Masquage des collections en fonction des gammes sélectionnées
   useEffect(() => {
      const selectedGammesIds = getSelectedGammesIds()
      let arr

      // Si aucune gamme sélectionnée, on va quand même afficher toutes les collections
      if (selectedGammesIds.length === 0) {
         arr = Object.keys(getRetailCollectionsInGammes(Object.keys(getRetailGammes())))
      } else {
         arr = Object.keys(getRetailCollectionsInGammes(selectedGammesIds))
      }

      // Pas de setCollections si la visibilité n'a pas changé : évite des re-renders inutiles
      // (et la boucle quand le useCallback du contexte est recréé à chaque render)
      setCollections(coll => {
         if (!coll) return coll
         const visibleSet = new Set(arr)
         let changed = false
         const next = coll.map(a_coll => {
            const newVisible = visibleSet.has(a_coll.id)
            if (a_coll.visible !== newVisible) {
               changed = true
               return { ...a_coll, visible: newVisible }
            }
            return a_coll
         })
         return changed ? next : coll
      })
   }, [getRetailCollectionsInGammes, getRetailGammes, getSelectedGammesIds, setCollections])

   // Affichage/Masquage des familles des modèles des collections sélectionnées.
   // Ne montre que les familles présentes parmi les modèles visibles pour le client courant.
   useEffect(() => {
      const selected_gammes_id = getSelectedGammesIds()
      const selected_cycles_id = getSelectedCyclesIds()
      let selected_collections_id = getSelectedCollectionsIds()

      // Cas particulier où on n'aurait pas choisi de collection, il faut donc ne proposer que les familles des collections des gammes retenues.
      if ((selected_gammes_id.length > 0) && (selected_collections_id.length === 0)) {
         selected_collections_id = Object.keys(getRetailCollectionsInGammes(selected_gammes_id))
      }
      let filter_res = filterOnCriteria({ gammes: selected_gammes_id, collections: selected_collections_id, cycles: selected_cycles_id })
      // Ne garder que les modèles visibles pour le type client courant + la matrice famille
      filter_res = filter_res.filter(model => isRetailModelVisibleForCustomer(model, effectiveCustomerType, hasExcluBoutique, customerForGrid))

      let famillesChanged = false
      setFamilles(fam => {
         if (!fam) return fam
         const next = fam.map(a_fam => {
            const show = filter_res.find((model) => model.fam === a_fam.id) !== undefined
            if (a_fam.visible !== show) {
               famillesChanged = true
               return { ...a_fam, visible: show }
            }
            return a_fam
         })
         return famillesChanged ? next : fam
      })
      if (famillesChanged) {
         setDoFilter(true)   // Prêt à filtrer, il ne reste qu'à s'occuper (éventuellement) des prix et du filtrage final
      }
   }, [filterOnCriteria, getRetailCollectionsInGammes, getSelectedCollectionsIds, getSelectedCyclesIds, getSelectedGammesIds, isRetailModelVisibleForCustomer, effectiveCustomerType, hasExcluBoutique, customerForGrid, setDoFilter, setFamilles])

   // Affichage/Masquage des typologies des modèles des familles sélectionnées
   useEffect(() => {
      const selected_gammes_id = getSelectedGammesIds()
      const selected_collections_id = getSelectedCollectionsIds()
      const selected_familles_id = getSelectedFamillesIds()
      const selected_cycles_id = getSelectedCyclesIds()

      let filter_res = filterOnCriteria({ gammes: selected_gammes_id, collections: selected_collections_id, familles: selected_familles_id, cycles: selected_cycles_id })
      filter_res = filter_res.filter(model => isRetailModelVisibleForCustomer(model, effectiveCustomerType, hasExcluBoutique, customerForGrid))

      setTypologies(typo => {
         if (!typo) return typo
         let changed = false
         const next = typo.map(a_typo => {
            const show = filter_res.find((model) => (model as any).typo === a_typo.id) !== undefined
            if (a_typo.visible !== show) {
               changed = true
               return { ...a_typo, visible: show }
            }
            return a_typo
         })
         return changed ? next : typo
      })
   }, [filterOnCriteria, getSelectedCollectionsIds, getSelectedCyclesIds, getSelectedFamillesIds, getSelectedGammesIds, isRetailModelVisibleForCustomer, effectiveCustomerType, hasExcluBoutique, customerForGrid, setTypologies])

   useEffect(() => {
      // On montre tous les cycles au départ
      setCycles(cycle => {
         if (!cycle) return cycle
         if (cycle.every(c => c.visible)) return cycle
         return cycle.map(a_cycle => ({ ...a_cycle, visible: true }))
      })
   }, [setCycles])

   // Calcul des bornes min/max de prix en fonction des critères sélectionnés (hors prix)
   useEffect(() => {
      if (!allMinMax) return

      const selected_gammes_id = getSelectedGammesIds()
      const selected_collections_id = getSelectedCollectionsIds()
      const selected_familles_id = getSelectedFamillesIds()
      const selected_cycles_id = getSelectedCyclesIds()

      const arr = filterOnCriteria({ gammes: selected_gammes_id, collections: selected_collections_id, familles: selected_familles_id, cycles: selected_cycles_id })

      const min_max = arr?.reduce((prev, model) => {
         const model_min_max = allMinMax[model.id]
         if (!model_min_max || (model_min_max.min === null) || (model_min_max.max === null)) {
            return { ...prev }
         } else {
            return {
               min: prev.min !== null ? Math.min(prev.min, model_min_max.min) : model_min_max.min,
               max: prev.max !== null ? Math.max(prev.max, model_min_max.max) : model_min_max.max,
            }
         }
      }, { min: null, max: null })

      if (minPrice !== min_max.min || maxPrice !== min_max.max) {
         setMinPrice((min_max.min < 100) ? 0 : min_max.min)
         setMaxPrice(min_max.max)
      }

      if ((min_max.min > minManualPrice) || (min_max.max < minManualPrice)) {
         setMinManualPrice(null)
      }
      if (min_max.max < maxManualPrice) {
         setMaxManualPrice(null)
      }
   }, [allMinMax, filterOnCriteria, getSelectedCollectionsIds, getSelectedCyclesIds, getSelectedFamillesIds, getSelectedGammesIds, maxManualPrice, maxPrice, minManualPrice, minPrice, setMaxManualPrice, setMaxPrice, setMinManualPrice, setMinPrice])

   const onPricesChange = useCallback((min, max) => {
      setMinManualPrice((min > minPrice) ? min : null)
      setMaxManualPrice((max < maxPrice) ? max : null)
      setDoFilter(true)
   }, [maxPrice, minPrice, setDoFilter, setMaxManualPrice, setMinManualPrice])

   const onFilter = useCallback(() => {
      console.debug('onFilter !!!')
      // setDoFilter(true)
      if (props.onFilter) {
         props.onFilter()
      }
   }, [props])

   const onGammeChange = useCallback((id, state) => {
      console.log('ON GAMME CHANGE')
      const arr = gammes.map((gamme) => {
         if (gamme.id === id) {
            return { ...gamme, checked: state }
         } else {
            return { ...gamme }
         }
      })
      setGammes(arr)
   }, [gammes, setGammes])

   const onCollectionChange = useCallback((id, state) => {
      const arr = collections.map((collection) => {
         if (collection.id === id) {
            return { ...collection, checked: state }
         } else {
            return { ...collection }
         }
      })
      setCollections(arr)
   }, [collections, setCollections])

   const onFamilleChange = useCallback((new_states) => {
      console.log(new_states, familles);
      const arr = familles.map((famille) => {
         console.log(famille.id, new_states[famille.id]);
         if (new_states[famille.id] !== undefined) {
            return { ...famille, checked: new_states[famille.id] }
         } else {
            return { ...famille }
         }
      })
      setFamilles(arr)
   }, [familles, setFamilles])

   const onTypologieChange = useCallback((id, state) => {
      const arr = typologies.map((typo) => {
         if (typo.id === id) {
            return { ...typo, checked: state }
         } else {
            return { ...typo }
         }
      })
      setTypologies(arr)
   }, [typologies, setTypologies])

   const onCycleChange = useCallback((id, state) => {
      const arr = cycles.map((cycle) => {
         if (cycle.id === id) {
            return { ...cycle, checked: state }
         } else {
            return { ...cycle }
         }
      })
      setCycles(arr)
   }, [cycles, setCycles])

   const onGammesClear = useCallback(() => {
      console.log('ON GAMME CLEAR')
      clearGammes()
   }, [clearGammes])

   const onCollectionsClear = useCallback(() => {
      clearCollections()
   }, [clearCollections])

   const onFamillesClear = useCallback(() => {
      clearFamilles()
   }, [clearFamilles])

   const onTypologiesClear = useCallback(() => {
      clearTypologies()
   }, [clearTypologies])

   const onExcluBoutiqueChange = useCallback((val) => {
      setExcluBoutique(val === 'all' ? null : val)
      setDoFilter(true)
   }, [setExcluBoutique, setDoFilter])

   const onCyclesClear = useCallback(() => {
      clearCycles()
   }, [clearCycles])

   const onHideSearchFilters = useCallback(() => {
      console.log('onHideTools')
      setIsSearchFilterVisible(false)
   }, [])

   useEffect(() => {
      // console.log('New setFilterPopup', excluBoutique)
      setFilterPopup(
         <CatalogFilter isRetail={true} onHide={onHideSearchFilters} visible={isSearchFilterVisible} onFilter={onFilter} price_min={minPrice} price_max={maxPrice} manual_price_min={minManualPrice} manual_price_max={maxManualPrice} onPricesChange={onPricesChange} showGammes={(paramCollectionId === null)} gammes={gammes} onGammeChange={onGammeChange} onGammesClear={onGammesClear} showCollections={(paramCollectionId === null)} collections={collections} onCollectionChange={onCollectionChange} onCollectionsClear={onCollectionsClear} familles={familles} onFamilleChange={onFamilleChange} onFamillesClear={onFamillesClear} typologies={typologies} onTypologieChange={onTypologieChange} onTypologiesClear={onTypologiesClear} cycles={cycles} onCycleChange={onCycleChange} onCyclesClear={onCyclesClear} showExcluBoutique={showExcluBoutiqueFilter} excluBoutique={excluBoutique} onExcluBoutiqueChange={onExcluBoutiqueChange} />
      )
   }, [collections, cycles, familles, typologies, gammes, isSearchFilterVisible, maxManualPrice, maxPrice, minManualPrice, minPrice, onCollectionChange, onCollectionsClear, onCycleChange, onCyclesClear, onFamilleChange, onFamillesClear, onFilter, onGammeChange, onGammesClear, onHideSearchFilters, onTypologieChange, onTypologiesClear, onPricesChange, paramCollectionId, showExcluBoutiqueFilter, excluBoutique, onExcluBoutiqueChange])

   useEffect(() => {
      setKeepSearchBarAccessible(true)
      setIsDarkLayerVisible(isSearchFilterVisible)
   }, [isSearchFilterVisible, setIsDarkLayerVisible, setKeepSearchBarAccessible])

   function onSearchChange(text) {
      setSearchText(text)
   }
   const onSearch = useCallback(() => {
      console.log('ON SEARCH')
      if (props.onSearch) {
         props.onSearch(searchText)
      }

   }, [props, searchText])

   const onToggleSearchFilters = useCallback(() => {
      setIsSearchFilterVisible(!isSearchFilterVisible)
   }, [isSearchFilterVisible])

   useEffect(() => {
      if (!isDarkLayerVisible) {
         setIsSearchFilterVisible(false)
      }
   }, [isDarkLayerVisible])

   return (
      <Fragment>
         <Searchbar text={searchText} onChange={onSearchChange} onSearch={onSearch} >
            <div className={styles_Searchbar.search_tools}>
               {props.extraTools}
               <button className={hasCriteriaEnabled ? styles_Searchbar.red : ''} onClick={onToggleSearchFilters}>
                  <FontAwesomeIcon icon={faSliders} />
               </button>
               {filterPopup}
            </div>
         </Searchbar>
      </Fragment>
   )
}

export default RetailCatalogSearchBar;
